/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate, faTrash, faX } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import ContactSetMembers from "./ContactSetMembers";
import { proxyAPI } from "../proxyAPI";
import CogIcon from "../../assets/icons/cog.svg2";
import MyPagination from "../Common/MyPagination";
import TextCollapse from "../Common/TextCollapse";
import GroupsIcon from "../../assets/icons/groups.svg2";
import { setCurrentModal } from "../store/slices/modalsSlice";
import DynamicIcon from "../../assets/icons/bolt.svg2";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import MyIcon from "../Common/MyIcon";
import { isUserPermitted } from "../helpers/pageProps";
import { toast } from "react-toastify";
import styled from "styled-components";

const SettingsMenu = styled.div`
  .delete-button {
    display: none;
  }
  &:hover {
    .delete-button {
      display: block;
    }
  }
`;

export default function ContactSetsList({
  contactSetButton,
  search,
  csType,
  orderBy,
  refresh,
  fullView = true,
  isModal,
  excludeUuids,
  uuids,
}) {
  const dispatch = useDispatch();
  const setContactSetModalData = (d) => {
    dispatch(setCurrentModal({ modal: "contactSet", data: d }));
  };
  const [contactSets, setContactSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [resetPagination, setResetPagination] = useState(Date.now());
  const uuidsKey = useMemo(() => (uuids || []).join(","), [uuids]);
  const excludeUuidsKey = useMemo(
    () => (excludeUuids || []).join(","),
    [excludeUuids]
  );

  const getContactSets = async (page = 0, perPage = 10) => {
    if (isUserPermitted("view_group")) {
      setLoading(true);

      const data = await proxyAPI({
        prefix: "contacts/v1",
        url: `/contact-sets?paginate.page=${page}&paginate.per_page=${perPage}&search_label=${search}&with_type=${csType}&order_by=${orderBy}${
          excludeUuids
            ? excludeUuids.map((uuid) => `&exclude_uuids=${uuid}`).join("")
            : ""
        }${uuids ? uuids.map((uuid) => `&uuids=${uuid}`).join("") : ""}`,
      });

      setTimeout(() => {
        setLoading(false);
        if (data?.pagination) {
          setContactSets(data.contactSets);
          setTotal(data.pagination.total);
        }
      }, 500);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      // setLoading(true);
      setResetPagination(Date.now());
    }, 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [search]);

  useEffect(() => {
    setResetPagination(Date.now());
  }, [csType, orderBy, refresh, uuidsKey, excludeUuidsKey]);

  return (
    <div className="h-100 d-flex flex-column position-relative p-3 rounded-3 bg-white">
      <FullScreenLoadingIndicator loading={loading} />
      {
        // eslint-disable-next-line no-nested-ternary
        contactSets.length > 0 ? (
          fullView ? (
            <div className="flex-grow-1">
              <div className="row m-0 row-cols-md-2 row-cols-lg-3 row-cols-xxl-4 gap-0 align-items-start">
                {contactSets.map((contactSet) => (
                  <div className="col p-1" key={contactSet.uuid}>
                    <div
                      className="card-light-gradient rounded-4 text-navy h-100 p-4 position-relative"
                      key={contactSet.uuid}
                    >
                      <SettingsMenu className="d-flex align-items-start settings-menu">
                        <h3 className="fw-bold">
                          {contactSet.label || contactSet.uuid.slice(0, 5)}
                          <div className="small opacity-50">
                            {contactSet.reference
                              ? `Ref: ${contactSet.reference}`
                              : ""}
                          </div>
                        </h3>
                        <div className="flex-grow-1"></div>

                        <div className="p-1 bg-purple">
                          {isModal ? (
                            <button
                              type="button"
                              onClick={() =>
                                setContactSetModalData({
                                  uuid: contactSet.uuid,
                                  onClose: getContactSets,
                                })
                              }
                              className="btn btn-link px-0"
                            >
                              <MyIcon
                                Icon={CogIcon}
                                width={20}
                                className="rounded-3"
                              />
                            </button>
                          ) : (
                            <Link
                              to={`/assets/contacts/groups/${contactSet.uuid}`}
                            >
                              <MyIcon
                                Icon={CogIcon}
                                width={20}
                                className="rounded-3"
                              />
                            </Link>
                          )}
                        </div>
                      </SettingsMenu>
                      <hr className="text-secondary" />
                      <div className="text-secondary">
                        <TextCollapse text={contactSet.description} />
                      </div>
                      <div className="">
                        <span className="bg-colored-reverse py-1 px-4 text-white d-inline-block my-2 rounded-2 text-uppercase small fw-bold">
                          {contactSet.type === "dynamic" ? (
                            <DynamicIcon
                              width={15}
                              className="text-white me-2"
                            />
                          ) : (
                            ""
                          )}
                          {contactSet.type}
                          <span className="float-end">
                            {contactSetButton?.(contactSet)}
                          </span>
                        </span>
                      </div>
                      <div className="mb-3 small">
                        Last updated at:{" "}
                        {new Date(contactSet.updatedAt).toLocaleString()}
                      </div>
                      <div className="bg-white py-2 rounded-3 d-flex justify-content-evenly">
                        <div className="text-secondary text-center">
                          <h5 className="fw-bold text-navy m-0">
                            {contactSet.membersCount || 0}
                          </h5>
                          <ContactSetMembers
                            uuid={contactSet.uuid}
                            label={
                              <span
                                className="fw-bold"
                                style={{ fontSize: "0.6rem" }}
                              >
                                CONTACTS
                              </span>
                            }
                          />
                        </div>
                        <div className="text-secondary text-center">
                          <h5 className="fw-bold text-navy m-0">
                            {contactSet.stats?.sms || 0}
                          </h5>
                          <span
                            className="fw-bold"
                            style={{ fontSize: "0.6rem" }}
                          >
                            SMS
                          </span>
                        </div>
                        <div className="text-secondary text-center">
                          <h5 className="fw-bold text-navy m-0">
                            {contactSet.stats?.voice || 0}
                          </h5>
                          <span
                            className="fw-bold"
                            style={{ fontSize: "0.6rem" }}
                          >
                            VOICE
                          </span>
                        </div>
                        <div className="text-secondary text-center">
                          <h5 className="fw-bold text-navy m-0">
                            {contactSet.stats?.email || 0}
                          </h5>
                          <span
                            className="fw-bold"
                            style={{ fontSize: "0.6rem" }}
                          >
                            EMAIL
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-grow-1">
              <table className="table">
                <thead>
                  <tr style={{ lineHeight: "26px" }}>
                    <th className="p-2"></th>
                    <th className="p-2">Group label</th>
                    <th className="p-2">Group type</th>
                    <th className="p-2">Description</th>
                    <th className="p-2">Member count</th>
                    <th className="p-2">Last updated</th>
                    <th className="p-2">Edit group</th>
                  </tr>
                </thead>
                <tbody>
                  {contactSets.map((contactSet) => (
                    <tr key={contactSet.uuid}>
                      <td className="p-2">{contactSetButton?.(contactSet)}</td>
                      <td className="p-2">
                        {contactSet.label || contactSet.uuid.slice(0, 5)}
                      </td>
                      <td className="p-2">{contactSet.type}</td>
                      <td className="p-2">{contactSet.description}</td>
                      <td className="p-2">
                        {contactSet.membersCount}{" "}
                        <ContactSetMembers
                          uuid={contactSet.uuid}
                          label="contacts"
                        />
                      </td>
                      <td className="p-2">
                        {new Date(contactSet.updatedAt).toLocaleString()}
                      </td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() =>
                            setContactSetModalData({
                              uuid: contactSet.uuid,
                              onClose: getContactSets,
                            })
                          }
                          className="btn btn-link p-0"
                        >
                          <CogIcon width={20} className="purple" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="text-center opacity-50">
            <div>
              <GroupsIcon width={100} className="my-5" />
            </div>
            <div>Nothing to show</div>
          </div>
        )
      }

      <MyPagination
        total={total}
        key={resetPagination}
        onPaginate={getContactSets}
      />
    </div>
  );
}
